
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useUploadContext } from './contexts/UploadContext';

const getValidationMessage = (
  uploadMethod: string,
  uploadType: string,
  validationStatus: string,
  file: File | null,
  title: string,
  abstract: string,
  category: string,
  isSubmitting: boolean,
  visibility: string
): string | null => {
  if (isSubmitting) return null;

  // Priority 1: arXiv URL not validated (public only)
  if (uploadType === 'external' && validationStatus !== 'valid') {
    return 'Please validate your arXiv URL first';
  }

  // Priority 2: No file in file upload flow
  if (uploadMethod === 'file' && !file) {
    return 'Please upload a PDF file';
  }

  // For private papers, file is all that's needed — metadata is auto-extracted
  if (visibility === 'private') {
    return null;
  }

  // Priority 3: Missing title (public only)
  if (!title.trim()) {
    return 'Please enter a paper title';
  }

  // Priority 4: Abstract too short (public only)
  if (abstract.trim().length < 10) {
    return 'Abstract must be at least 10 characters';
  }

  // Priority 5: No category (public only)
  if (!category) {
    return 'Please select a field/category';
  }

  return null;
};

export const FormSubmitSection: React.FC = () => {
  const navigate = useNavigate();
  const { 
    isSubmitting, 
    uploadType, 
    validationStatus, 
    requestReview,
    uploadMethod,
    file,
    title,
    abstract,
    category,
    visibility
  } = useUploadContext();

  const validationMessage = getValidationMessage(
    uploadMethod,
    uploadType,
    validationStatus,
    file,
    title,
    abstract,
    category,
    isSubmitting,
    visibility
  );

  const isDisabled = isSubmitting || !!validationMessage;

  // For private uploads without user-provided title, use a different label
  const isPrivateMinimal = visibility === 'private' && !title.trim();

  return (
    <div className="flex flex-col items-end gap-2 pt-6 border-t border-border">
      <div className="flex justify-end gap-4 w-full">
        <Button type="button" variant="outline" onClick={() => navigate('/')}>
          Cancel
        </Button>
        <Button 
          type="submit" 
          disabled={isDisabled}
        >
          {isSubmitting ? 'Submitting...' : (
            isPrivateMinimal 
              ? 'Upload for AI Review'
              : uploadMethod === 'file' 
                ? (requestReview ? 'Upload & Request Review' : 'Upload for Review')
                : (requestReview ? 'Link & Request Review' : 'Link Paper')
          )}
        </Button>
      </div>
      {validationMessage && !isSubmitting && (
        <p className="text-sm text-muted-foreground">
          {validationMessage}
        </p>
      )}
    </div>
  );
};
